'use client';
import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/lib/LanguageContext';
import { getTranslations } from '@/lib/i18n';

const HeroSection: React.FC = () => {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const h = t.home;
  const [isVisible, setIsVisible] = useState(false);
  const [lineVisible, setLineVisible] = useState(false);

  useEffect(() => {
    const timer1 = setTimeout(() => setIsVisible(true), 100);
    const timer2 = setTimeout(() => setLineVisible(true), 600);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const heroTitle1 = language === 'fr' ? 'Technologie' : 'Tecnología';
  const heroTitle2 = language === 'fr' ? 'avec Objectif.' : 'con Propósito.';
  const heroSubtitleText = language === 'fr' ? 'INNOBOX développe et opère des solutions technologiques de pointe pour les entreprises qui exigent les plus hauts standards d\'innovation, de précision et d\'excellence.' : 'INNOBOX desarrolla y opera soluciones tecnológicas de vanguardia para empresas que exigen los más altos estándares de innovación, precisión y excelencia.';
  const trustedLabel = language === 'fr' ? 'Certifié par' : 'Certificado por';
  const ctaLabel = language === 'fr' ? 'Nos Services' : 'Nuestros Servicios';
  const ctaLabel2 = language === 'fr' ? 'Nous Contacter' : 'Contáctenos';

  const partners = [
  { src: 'https://innobox.cl/wp-content/uploads/2025/05/1.png', alt: 'Partner tecnológico de Innobox' },
  { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1b43c8e63-1765286150777.png", alt: 'Partner de soluciones digitales' },
  { src: 'https://innobox.cl/wp-content/uploads/2025/05/5-1.png', alt: 'Partner de tecnología Innobox' },
  { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ccfe0794-1772147307895.png", alt: 'Partner de infraestructura' },
  { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1307187cb-1772085804087.png", alt: 'Partner de software' },
  { src: 'https://innobox.cl/wp-content/uploads/2025/05/lenovo2-1.png', alt: 'Lenovo partner de Innobox' }];


  return (
    <section
      id="inicio"
      className="relative overflow-hidden bg-[#141618]"
      style={{ minHeight: '100vh', paddingTop: '0' }}>
      
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://innobox.cl/wp-content/uploads/2025/04/Imagen-Central-Innobox-Web-1.png"
          alt="Innobox technology background"
          className="w-full h-full object-cover"
          style={{ opacity: 0.15 }} />
        
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #141618 0%, rgba(20,22,24,0.6) 40%, rgba(20,22,24,0.85) 80%, #141618 100%)' }} />
      </div>

      {/* Grid column lines */}
      <div className="absolute inset-0 pointer-events-none z-1 overflow-hidden">
        <div className="absolute top-0 bottom-0 left-[25%] w-px bg-white/5" />
        <div className="absolute top-0 bottom-0 left-[50%] w-px bg-white/5" />
        <div className="absolute top-0 bottom-0 left-[75%] w-px bg-white/5" />
        {/* Horizontal lines */}
        <div className="absolute left-0 right-0 top-[33%] h-px bg-white/4" />
        <div className="absolute left-0 right-0 top-[66%] h-px bg-white/4" />
        {/* Corner dots */}
        <div className="absolute top-[33%] left-[25%] w-[5px] h-[5px] rounded-full bg-white/15 -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute top-[33%] left-[75%] w-[5px] h-[5px] rounded-full bg-white/15 -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute top-[66%] left-[25%] w-[5px] h-[5px] rounded-full bg-white/15 -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute top-[66%] left-[75%] w-[5px] h-[5px] rounded-full bg-white/15 -translate-x-1/2 -translate-y-1/2" />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Hero text - centered vertically */}
        <div className="flex-1 flex flex-col justify-center" style={{ paddingTop: '120px', paddingBottom: '80px' }}>
          <div className="max-w-[1280px] mx-auto px-6 w-full">
            <div className="max-w-[800px]">
              {/* Eyebrow */}
              <div
                className={`flex items-center gap-3 mb-8 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`
                }>
                <div className="w-8 h-px bg-[#abd233]" />
                <span style={{ fontSize: '12px', letterSpacing: '0.1em', color: '#abd233', textTransform: 'uppercase' }}>
                  INNOBOX · Transformación Digital
                </span>
              </div>

              {/* Main headline */}
              <div className="overflow-hidden mb-2">
                <h1
                  className={`text-white transition-all duration-700 delay-100 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'}`
                  }
                  style={{
                    fontSize: 'clamp(52px, 7vw, 96px)',
                    fontWeight: 400,
                    lineHeight: 1,
                    letterSpacing: '-0.02em'
                  }}>
                  {heroTitle1}
                </h1>
              </div>
              <div className="overflow-hidden mb-10">
                <h1
                  className={`transition-all duration-700 delay-200 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'}`
                  }
                  style={{
                    fontSize: 'clamp(52px, 7vw, 96px)',
                    fontWeight: 400,
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                    color: '#abd233'
                  }}>
                  {heroTitle2}
                </h1>
              </div>

              {/* Horizontal rule */}
              <div
                className={`h-px bg-white/10 mb-8 transition-all duration-700 delay-300 ${
                lineVisible ? 'w-full opacity-100' : 'w-0 opacity-0'}`
                } />
              

              {/* Description */}
              <p
                className={`text-white/60 mb-10 transition-all duration-700 delay-400 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`
                }
                style={{ fontSize: '19px', lineHeight: 1.5, maxWidth: '560px' }}>
                {heroSubtitleText}
              </p>

              {/* CTAs */}
              <div
                className={`flex flex-wrap gap-4 transition-all duration-700 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`
                }>
                <a
                  href="#servicios"
                  className="group relative inline-flex items-center overflow-hidden"
                  style={{
                    border: '1px solid rgba(255,255,255,0.25)',
                    padding: '14px 28px',
                    fontSize: '13px',
                    color: '#ffffff',
                    letterSpacing: '0.02em',
                    transition: 'all 300ms ease-out'
                  }}>
                  <span
                    className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"
                    style={{ zIndex: 0 }} />
                  
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-[#141618]">
                    {ctaLabel}
                  </span>
                </a>
                <a
                  href="/contact"
                  className="group relative inline-flex items-center overflow-hidden"
                  style={{
                    border: '1px solid #abd233',
                    padding: '14px 28px',
                    fontSize: '13px',
                    color: '#abd233',
                    letterSpacing: '0.02em',
                    transition: 'all 300ms ease-out'
                  }}>
                  <span
                    className="absolute inset-0 bg-[#abd233] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"
                    style={{ zIndex: 0 }} />
                  
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-[#141618]">
                    {ctaLabel2}
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom partners bar */}
        <div
          className={`border-t border-white/6 transition-all duration-700 delay-700 ${
          isVisible ? 'opacity-100' : 'opacity-0'}`
          }>
          <div className="max-w-[1280px] mx-auto px-6 py-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <span
                className="text-white/40 flex-shrink-0"
                style={{ fontSize: '12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                {trustedLabel}
              </span>
              <div className="flex items-center gap-8 overflow-hidden">
                {partners.slice(0, 4).map((p, i) =>
                <img
                  key={i}
                  src={p.src}
                  alt={p.alt}
                  className="h-6 w-auto object-contain opacity-40 hover:opacity-70 transition-opacity duration-300"
                  style={{ filter: 'brightness(0) invert(1)' }} />

                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;